import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { getOnboardingFieldOrder } from "../lib/constants/onboarding.js";

const OnboardingForm = ({
  questions = [],
  collapsiblePrompt,
  onSubmit,
  primaryColor = "#2563eb",
  isRtl = false,
  isDark = false,
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sortedQuestions = [...questions].sort(
    (a, b) =>
      getOnboardingFieldOrder(a.fieldType) - getOnboardingFieldOrder(b.fieldType)
  );

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    let isValid = true;

    sortedQuestions.forEach((q) => {
      const key = q.fieldType || "question";
      const value = (formData[key] ?? "").trim();
      const label =
        q.collectionPrompt || q.askToType || q.question || q.label || key;
      const isRequired = q.required === true || q.required === "true";

      if (!value) {
        if (isRequired) {
          if (key === "email") newErrors[key] = "Email is required";
          else if (key === "fullName" || key === "name")
            newErrors[key] = "Name is required";
          else if (key === "phone" || key === "mobileno")
            newErrors[key] = "Phone number is required";
          else newErrors[key] = `${label} is required`;
          isValid = false;
        }
      } else {
        if (key === "fullName" || key === "name") {
          if (value.length < 2) {
            newErrors[key] = "Please enter a valid name";
            isValid = false;
          }
        }
        if (key === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          newErrors[key] = "Please enter a valid email address";
          isValid = false;
        }
        if (
          (key === "phone" || key === "mobileno") &&
          !/^[+]?[\d\s-]{8,15}$/.test(value)
        ) {
          newErrors[key] = "Please enter a valid phone number";
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className={`flex flex-col items-center justify-center p-5 h-full min-h-0 z-50 relative ${
        isDark ? "" : "bg-gray-50"
      }`}
      style={isDark ? { backgroundColor: "#10141C" } : undefined}
    >
      <div
        className={`w-full max-w-sm rounded-2xl p-6 transition-colors duration-300 ${
          isDark ? "shadow-lg" : "bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-100"
        }`}
        style={
          isDark
            ? {
                backgroundColor: "#1E232B",
                border: "1px solid #2A303C",
                borderRadius: "14px",
              }
            : undefined
        }
      >
        {collapsiblePrompt && (
          <div className="mb-5 text-left">
            <h3
              className={`transition-colors duration-300 ${
                isDark
                  ? "text-white font-bold text-lg"
                  : "text-base font-medium text-[#4A5568]"
              }`}
            >
              {collapsiblePrompt}
            </h3>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {sortedQuestions.map((q, index) => {
            const key = q.fieldType || `question_${index}`;
            const label =
              q.collectionPrompt || q.askToType || q.question || q.label;

            if (!label?.trim()) return null;

            let type = "text";
            if (key === "email") type = "email";
            if (key === "phone" || key === "mobileno") type = "tel";

            const hasError = errors[key];
            const inputBase = `w-full h-12 rounded-lg text-[15px] px-4 outline-none border transition-colors focus:ring-0 ${
              isDark
                ? "text-white placeholder:text-[#A2A9B2] focus:border-[#5A6069]"
                : "bg-white text-gray-900 placeholder:text-[#8C8C8C] focus:border-[#B0B0B0]"
            }`;
            const inputError = hasError
              ? "!border-red-500 focus:!border-red-500"
              : "";

            return (
              <div key={key} className="space-y-1">
                <input
                  placeholder={label}
                  type={type}
                  value={formData[key] ?? ""}
                  onChange={(e) => handleChange(key, e.target.value)}
                  className={`${inputBase} ${inputError}`}
                  style={{
                    direction: isRtl ? "rtl" : "ltr",
                    ...(isDark
                      ? {
                          backgroundColor: "#272C35",
                          borderWidth: "1px",
                          borderStyle: "solid",
                          borderColor: hasError ? undefined : "#3F444C",
                        }
                      : {
                          borderWidth: "1px",
                          borderStyle: "solid",
                          borderColor: hasError ? undefined : "#E0E0E0",
                        }),
                  }}
                />
                {hasError && (
                  <p className="text-xs text-red-500 px-1">{errors[key]}</p>
                )}
              </div>
            );
          })}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full h-12 text-sm border-none  font-semibold shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all rounded-xl mt-4 text-white"
            style={{
              backgroundColor: primaryColor,
            }}
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              "Start Chat"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default OnboardingForm;
